// Tài liệu cá nhân: được chia sẻ với tôi / tôi tạo / góp ý dự thảo, xem gần đây, nhãn cá nhân, dạng lưới, xuất Excel
import { useMemo, useState } from 'react';
import { Button, Dropdown, Input, Pagination, Popconfirm, Select, Table, Tabs, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Download, Info, LayoutGrid, List, Pencil, Plus, Search, Settings, Tag, Trash, Trash2, X } from 'lucide-react';
import DocShell from 'pages/Documents/components/DocShell';
import { LabelPicker, LabelTags, QuickInfoDrawer } from 'pages/Documents/components/QuickInfo';
import useDocActions from 'pages/Documents/components/useDocActions';
import useDocsStore from 'pages/Documents/store';
import { deptName, type Doc, type DocState } from 'pages/Documents/data';
import { canViewDoc, docState, matches, recipientIds } from 'pages/Documents/logic';
import { FileTypeIcon, SeenPill, StatePill, docLink, exportDocsExcel, fmtDate, fmtDateTime, fmtEffective, useDocAuth, usePeople } from 'pages/Documents/utils';

const VIEW_KEY = 'docs-personal-view';
type TabKey = 'shared' | 'mine' | 'comment';

export default function PersonalDocuments() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can, me, viewer } = useDocAuth();
  const actions = useDocActions();
  const people = usePeople();
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);
  const allDocs = useDocsStore(s => s.docs);
  const myLabels = useDocsStore(s => s.myLabels);
  const labelList = useDocsStore(s => s.labelList);
  const deleteLabel = useDocsStore(s => s.deleteLabel);

  const [view, setView] = useState<'list' | 'grid'>(() => { try { return localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list'; } catch { return 'list'; } });
  const [info, setInfo] = useState<Doc | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const tab = (params.get('tab') as TabKey) || 'shared';
  const q = params.get('q') || '';
  const type = params.get('type') || '';
  const state = params.get('state') || '';
  const seen = params.get('seen') || '';
  const label = params.get('label') || '';
  const setParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
    setPage(1);
  };
  const changeView = (v: 'list' | 'grid') => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* bỏ qua */ } };

  const visible = useMemo(() => allDocs.filter(d => !d.deletedAt && canViewDoc(d, viewer, people)), [allDocs, viewer, people]);
  const groups = useMemo(() => {
    const sharedWithMe = (d: Doc) => d.createdBy !== me.id && recipientIds(d, people).includes(me.id);
    return {
      shared: visible.filter(d => sharedWithMe(d) && (d.status === 'published' || d.status === 'revoked')),
      mine: visible.filter(d => d.createdBy === me.id),
      comment: visible.filter(d => sharedWithMe(d) && d.status === 'draft' && d.draftMode),
    };
  }, [visible, me.id, people]);

  const rows = groups[tab].filter(d =>
    matches(q, d.title, d.code, d.number) &&
    (!type || d.typeId === type) &&
    (!state || docState(d) === state) &&
    (!seen || (seen === 'seen' ? !!d.views[me.id] : !d.views[me.id])) &&
    (!label || (myLabels[d.id] || []).includes(label)),
  ).sort((a, b) => (b.approvedAt || b.updatedAt).localeCompare(a.approvedAt || a.updatedAt));

  const unseenCount = groups.shared.filter(d => !d.views[me.id]).length;

  // Xem gần đây: lần xem mới nhất của tôi, tối đa 6 tài liệu
  const recent = useMemo(() => {
    const last: { doc: Doc; at: string }[] = [];
    visible.forEach(d => {
      const mine = d.access.filter(a => a.userId === me.id && a.action === 'view');
      if (mine.length) last.push({ doc: d, at: mine[mine.length - 1].at });
    });
    return last.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 6);
  }, [visible, me.id]);

  const stateOptions: DocState[] = tab === 'mine' ? ['draft', 'pending', 'rejected', 'upcoming', 'active', 'expired', 'replaced', 'revoked'] : tab === 'shared' ? ['upcoming', 'active', 'expired', 'replaced', 'revoked'] : [];
  const STATE_LABEL: Record<DocState, string> = { draft: 'Dự thảo', pending: 'Chờ duyệt', rejected: 'Bị từ chối', upcoming: 'Chưa hiệu lực', active: 'Còn hiệu lực', expired: 'Hết hiệu lực', replaced: 'Bị thay thế', revoked: 'Đã thu hồi' };

  const exportExcel = () => {
    if (!rows.length) return message.warning('Không có dữ liệu để xuất');
    exportDocsExcel(rows, types, categories, people, `tai-lieu-ca-nhan_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  };

  const rowActions = (d: Doc) => (
    <div className="flex items-center justify-center gap-2.5" onClick={e => e.stopPropagation()}>
      <Tooltip title="Thông tin"><button onClick={() => setInfo(d)} className="text-slate-400 hover:text-[#357f96]"><Info size={15} /></button></Tooltip>
      <LabelPicker docId={d.id} />
      {actions.perm.edit(d) && <Tooltip title={d.status === 'published' ? 'Sửa phân quyền' : 'Sửa'}><button onClick={() => navigate(`/add-new-document?id=${d.id}`)} className="text-slate-400 hover:text-[#357f96]"><Pencil size={15} /></button></Tooltip>}
      {actions.perm.delete(d) && <Tooltip title="Xóa"><button onClick={() => actions.remove(d)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button></Tooltip>}
    </div>
  );

  const columns: ColumnsType<Doc> = [
    { title: 'Mã tài liệu', dataIndex: 'code', width: 190, render: (v, d) => <Link to={docLink(d.id)} className="text-xs font-medium text-[#357f96] hover:underline">{v}</Link> },
    {
      title: 'Tên tài liệu', dataIndex: 'title', width: 320,
      render: (v, d) => {
        const unseen = tab !== 'mine' && !d.views[me.id];
        return (
          <div className="flex items-start gap-2">
            {unseen && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-red-400" />}
            <div className="min-w-0">
              <Link to={docLink(d.id)} className={`text-[13px] hover:text-[#357f96] ${unseen ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>{v}</Link>
              <div className="mt-0.5"><LabelTags docId={d.id} /></div>
            </div>
          </div>
        );
      },
    },
    { title: 'Hiệu lực', key: 'eff', width: 170, render: (_v, d) => <span className="text-xs text-slate-600">{fmtEffective(d)}</span> },
    { title: 'Loại', dataIndex: 'typeId', width: 120, render: v => <span className="text-xs text-slate-600">{types.find(t => t.id === v)?.name}</span> },
    { title: 'Đơn vị ban hành', dataIndex: 'unitId', width: 150, render: v => <span className="text-xs text-slate-600">{deptName(v)}</span> },
    { title: 'Trạng thái', key: 'state', width: 140, render: (_v, d) => <StatePill doc={d} /> },
    ...(tab !== 'mine' ? [{ title: 'Tình trạng', key: 'seen', width: 100, align: 'center' as const, render: (_v: unknown, d: Doc) => <SeenPill seen={!!d.views[me.id]} /> }] : []),
    { title: 'Hành động', key: 'act', width: 130, align: 'center', render: (_v, d) => rowActions(d) },
  ];

  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <DocShell
      title="Tài liệu cá nhân"
      subtitle={unseenCount ? <span><b className="text-red-500">{unseenCount}</b> tài liệu được chia sẻ bạn chưa xem</span> : 'Bạn đã xem hết tài liệu được chia sẻ'}
      actions={can('create') && <Button type="primary" icon={<Plus size={16} />} onClick={() => navigate('/add-new-document')}>Tạo mới</Button>}
    >
      {actions.modals}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-2.5">
          <div className="flex items-center rounded-md border border-slate-200 p-0.5">
            <Tooltip title="Dạng danh sách"><button onClick={() => changeView('list')} className={`rounded px-2 py-1 ${view === 'list' ? 'bg-[#ecf6f9] text-[#2c6b7f]' : 'text-slate-400'}`}><List size={16} /></button></Tooltip>
            <Tooltip title="Dạng lưới"><button onClick={() => changeView('grid')} className={`rounded px-2 py-1 ${view === 'grid' ? 'bg-[#ecf6f9] text-[#2c6b7f]' : 'text-slate-400'}`}><LayoutGrid size={16} /></button></Tooltip>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-slate-600">
            <Dropdown
              trigger={['click']}
              menu={{
                items: labelList.length ? labelList.map(l => ({
                  key: l,
                  label: (
                    <div className="flex min-w-[180px] items-center justify-between gap-3">
                      <span className={label === l ? 'font-semibold text-[#2c6b7f]' : ''}>{l}</span>
                      <Popconfirm title={`Xóa nhãn “${l}”?`} description="Nhãn sẽ được gỡ khỏi mọi tài liệu." okText="Xóa" cancelText="Hủy" onConfirm={e => { e?.stopPropagation(); deleteLabel(l); if (label === l) setParam({ label: '' }); }}>
                        <X size={13} className="text-slate-400 hover:text-red-500" onClick={e => e.stopPropagation()} />
                      </Popconfirm>
                    </div>
                  ),
                  onClick: () => setParam({ label: label === l ? '' : l }),
                })) : [{ key: 'none', label: 'Chưa có nhãn — gắn nhãn ở biểu tượng 🏷 từng tài liệu', disabled: true }],
              }}
            >
              <button className={`flex items-center gap-1.5 hover:text-[#357f96] ${label ? 'text-[#2c6b7f]' : ''}`}><Tag size={14} className="text-slate-400" /> Nhãn{label ? `: ${label}` : ''}</button>
            </Dropdown>
            {can('delete') && <button onClick={() => navigate('/document-trash')} className="flex items-center gap-1.5 hover:text-[#357f96]"><Trash size={14} className="text-slate-400" /> Thùng rác</button>}
            {can('export') && <button onClick={exportExcel} className="flex items-center gap-1.5 hover:text-[#357f96]"><Download size={14} className="text-slate-400" /> Export</button>}
            {can('config') && <button onClick={() => navigate('/document-type')} className="flex items-center gap-1.5 hover:text-[#357f96]"><Settings size={14} className="text-slate-400" /> Cài đặt</button>}
          </div>
        </div>

        {recent.length > 0 && (
          <div className="border-b border-slate-200 bg-slate-50/60 p-4">
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Xem gần đây</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {recent.map(({ doc, at }) => (
                <Link key={doc.id} to={docLink(doc.id)} className="group flex flex-col justify-between rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-all hover:border-[#3e91ab]/50 hover:shadow-md">
                  <div className="mb-2 flex h-12 items-center justify-center"><FileTypeIcon ext={doc.files[0]?.ext || 'pdf'} size={30} /></div>
                  <div className="truncate text-xs font-semibold text-slate-800" title={doc.title}>{doc.title}</div>
                  <div className="mt-0.5 flex justify-between text-[10px] text-slate-400"><span>{fmtDateTime(at)}</span></div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Tabs
          activeKey={tab} onChange={k => setParams(k === 'shared' ? new URLSearchParams() : new URLSearchParams({ tab: k }), { replace: true })}
          className="px-4 [&_.ant-tabs-nav]:!mb-0"
          items={[
            { key: 'shared', label: <span>Được chia sẻ với tôi <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-xs">{groups.shared.length}</span></span> },
            { key: 'mine', label: <span>Tôi tạo <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-xs">{groups.mine.length}</span></span> },
            { key: 'comment', label: <span>Góp ý dự thảo <span className={`ml-1 rounded-full px-1.5 text-xs ${groups.comment.length ? 'bg-amber-100 text-amber-700' : 'bg-slate-100'}`}>{groups.comment.length}</span></span> },
          ]}
        />
        <div className="flex flex-wrap items-center gap-2 border-y border-slate-100 bg-slate-50/60 p-3">
          <Input allowClear prefix={<Search size={15} className="text-slate-400" />} placeholder="Tên, mã tài liệu…" value={q} onChange={e => setParam({ q: e.target.value })} className="w-full sm:w-64" />
          <Select value={type} onChange={v => setParam({ type: v })} className="w-40" options={[{ value: '', label: 'Tất cả loại' }, ...types.map(t => ({ value: t.id, label: t.name }))]} />
          {stateOptions.length > 0 && <Select value={state} onChange={v => setParam({ state: v })} className="w-40" options={[{ value: '', label: 'Tất cả trạng thái' }, ...stateOptions.map(s => ({ value: s, label: STATE_LABEL[s] }))]} />}
          {tab !== 'mine' && <Select value={seen} onChange={v => setParam({ seen: v })} className="w-36" options={[{ value: '', label: 'Tình trạng xem' }, { value: 'unseen', label: 'Chưa xem' }, { value: 'seen', label: 'Đã xem' }]} />}
          {label && <span className="flex items-center gap-1 rounded-full bg-[#ecf6f9] px-2.5 py-1 text-xs text-[#2c6b7f]">Nhãn: {label}<X size={13} className="cursor-pointer" onClick={() => setParam({ label: '' })} /></span>}
        </div>

        {view === 'list' ? (
          <Table<Doc>
            rowKey="id" columns={columns} dataSource={rows} scroll={{ x: 1100 }} size="middle"
            onRow={d => ({ onDoubleClick: () => navigate(docLink(d.id)) })}
            locale={{ emptyText: tab === 'comment' ? 'Không có bản dự thảo nào cần góp ý' : tab === 'mine' ? 'Bạn chưa tạo tài liệu nào' : 'Chưa có tài liệu được chia sẻ phù hợp' }}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50], showTotal: (t, r) => `${r[0]}-${r[1]} trong ${t} tài liệu` }}
          />
        ) : (
          <div className="p-4">
            {rows.length === 0 ? (
              <div className="py-12 text-center text-sm text-slate-400">Không có tài liệu phù hợp</div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                  {pageRows.map(d => {
                    const unseen = tab !== 'mine' && !d.views[me.id];
                    return (
                      <div key={d.id} onClick={() => navigate(docLink(d.id))} className="group relative flex cursor-pointer flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:border-[#3e91ab]/50 hover:shadow-md">
                        {unseen && <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-red-400" />}
                        <div className="mb-3 flex items-center gap-3">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#ecf6f9]"><FileTypeIcon ext={d.files[0]?.ext || 'pdf'} size={24} /></div>
                          <div className="min-w-0">
                            <div className="truncate text-[11px] text-slate-400">{d.code}</div>
                            <div className={`line-clamp-2 text-[13px] ${unseen ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>{d.title}</div>
                          </div>
                        </div>
                        <div className="mb-3 flex flex-wrap items-center gap-1"><StatePill doc={d} /><LabelTags docId={d.id} /></div>
                        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-2 text-[11px] text-slate-400">
                          <span>{fmtDate(d.effectiveFrom)} · {d.files.length} file</span>
                          {rowActions(d)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 flex justify-end">
                  <Pagination current={page} pageSize={pageSize} total={rows.length} showSizeChanger pageSizeOptions={[12, 24, 48]}
                    onChange={(p, s) => { setPage(p); setPageSize(s); }} showTotal={(t, r) => `${r[0]}-${r[1]} trong ${t} tài liệu`} />
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <QuickInfoDrawer doc={info} onClose={() => setInfo(null)} />
    </DocShell>
  );
}
